// Inventario de acopio (Inteligencia › Acopio).
// En cada día se juntan las cajas de la misma clase que están en el mismo lugar en distintas fotos (= un objeto
// acopiado) y cuenta solo lo que se ve en al menos el 30 % de las fotos del día: así no suma lo que pasó un rato
// (una carretilla, un camión) ni se pierde lo que una persona o un camión tapó un momento.
import { fmtKey, arHour } from '../iaData';
import { iou } from '../entregas';
import { MATERIALES, MAT, porDia, plural } from './datosV3';

const CONF = 0.3;
const cap1 = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const PERSISTE = 0.3; // visto en al menos el 30 % de las fotos del día
export const DIA_COMPLETO = 8; // con menos fotos de jornada el día se muestra pero no se compara

// Inventario de un día para una clase: objetos persistentes (mismo lugar en varias fotos)
export function inventarioDia(fotos, cls) {
  const slots = [];
  fotos.forEach((p, i) => {
    for (const d of p.dets) {
      if (d.cls !== cls || d.conf < CONF) continue;
      let mejor = null, mv = 0.25;
      for (const s of slots) {
        if (s.fotos.has(i)) continue;
        const v = iou(s.box, d);
        if (v > mv) { mejor = s; mv = v; }
      }
      if (mejor) { mejor.fotos.add(i); mejor.obs.push({ p, d }); } else slots.push({ box: d, fotos: new Set([i]), obs: [{ p, d }] });
    }
  });
  const min = Math.max(2, PERSISTE * fotos.length);
  const quedan = slots.filter((s) => s.fotos.size >= min);
  const med = (xs) => { const s = [...xs].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : 0; };
  const area = quedan.reduce((a, s) => a + med(s.obs.map((o) => o.d.area || 0)), 0);
  // Foto que mejor lo muestra: la última del día donde se ven más de esos objetos
  let foto = null, visto = -1;
  fotos.forEach((p, i) => { const n = quedan.filter((s) => s.fotos.has(i)).length; if (n >= visto) { visto = n; foto = p; } });
  const cajas = foto ? quedan.flatMap((s) => s.obs.filter((o) => o.p === foto).map((o) => o.d)) : [];
  return { n: quedan.length, area, foto, cajas, slots: quedan };
}

// Reglas de confianza (17/10, Jairo: el acopio tiene que ser muy confiable):
//  - Si los datos traen la verificación visual (`acopio` en cada detección: clase mirada en un recorte, o false),
//    se cuentan SOLO esas detecciones y con la clase que dijo la mirada, no la del detector.
//  - Un objeto cuenta si está en el mismo lugar 2 días completos seguidos (o más).
//  - "Nuevo" / "se fue" se afirma solo si en el otro día, a una hora parecida (±1 h), hay 3 fotos o más donde NO está:
//    un solo hueco del detector o un cambio de luz no alcanzan.
const HORA = 3600e3;
const minDelDia = (ms) => { const d = new Date(ms - 3 * HORA); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
// Fotos del otro día, a ±1 h, donde en ese lugar el detector no ve NINGÚN material (de cualquier clase: el subtipo no es
// confiable) — con todas las detecciones, no solo las verificadas.
const MATS = new Set(MATERIALES);
const ausenteEn = (fotosDia, box, horas) => fotosDia.filter((p) => horas.some((m) => Math.abs(minDelDia(p.ms) - m) <= 60)
  && !p.dets.some((d) => MATS.has(d.cls) && d.conf >= CONF && iou(d, box) > 0.15)).length;
const verificados = (fotos) => {
  if (!fotos.some((p) => p.dets.some((d) => d.acopio !== undefined))) return null;
  return fotos.map((p) => ({ ...p, dets: p.dets.filter((d) => typeof d.acopio === 'string').map((d) => ({ ...d, cls: d.acopio })) }));
};
const rehacer = (x, slots) => {
  const med = (xs) => { const s = [...xs].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : 0; };
  const area = slots.reduce((a, s) => a + med(s.obs.map((o) => o.d.area || 0)), 0);
  const cajas = x.foto ? slots.flatMap((s) => s.obs.filter((o) => o.p === x.foto).map((o) => o.d)) : [];
  return { ...x, n: slots.length, area, cajas, slots };
};
// Foto del día `x` a la hora más parecida a la de `ref`
const fotoParecida = (fs, ref) => (ref && fs.length ? fs.reduce((a, p) => (Math.abs(minDelDia(p.ms) - minDelDia(ref.ms)) < Math.abs(minDelDia(a.ms) - minDelDia(ref.ms)) ? p : a)) : null);

export function calcular(fotosIn) {
  const ver = verificados(fotosIn);
  const fotos = ver || fotosIn;
  const dias = [...porDia(fotos.filter((p) => { const h = arHour(p.ms); return h >= 7 && h < 19; })).entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  const fotosDe = new Map(dias);
  const crudasDe = porDia(fotosIn);
  const porClase = {};
  for (const c of MATERIALES) {
    const xs = dias.map(([k, fs]) => ({ dia: k, nFotos: fs.length, completo: fs.length >= DIA_COMPLETO, ...inventarioDia(fs, c) }));
    // estable: el mismo lugar el día completo anterior o el siguiente
    const comp = xs.map((x, i) => i).filter((i) => xs[i].completo);
    porClase[c] = xs.map((x, i) => {
      if (!x.completo) return x;
      const j = comp.indexOf(i), vec = [xs[comp[j - 1]], xs[comp[j + 1]]].filter(Boolean);
      return rehacer(x, x.slots.filter((s) => vec.some((v) => v.slots.some((o) => iou(o.box, s.box) > 0.25))));
    });
  }
  // Lo que se vio en algún día completo (si todavía no hay días completos, en cualquiera)
  const hayCompletos = dias.some(([, fs]) => fs.length >= DIA_COMPLETO);
  const presentes = MATERIALES.filter((c) => porClase[c].some((x) => x.n > 0 && (x.completo || !hayCompletos)));
  // Avisos entre días completos seguidos: objetos que se fueron / llegaron, comprobados a horas parecidas (±1 h) y con
  // al menos 3 fotos del otro día donde no están. Las pilas también se comparan objeto por objeto (no por superficie:
  // la superficie cambia con la luz y las sombras).
  const avisos = [];
  for (const c of presentes) {
    const xs = porClase[c].filter((x) => x.completo);
    const m = MAT[c];
    for (let i = 1; i < xs.length; i++) {
      const a = xs[i - 1], b = xs[i];
      const horas = (s) => s.obs.map((o) => minDelDia(o.p.ms));
      const fueron = a.slots.filter((s) => !b.slots.some((o) => iou(o.box, s.box) > 0.15) && ausenteEn(crudasDe.get(b.dia) || [], s.box, horas(s)) >= 3);
      const llegaron = b.slots.filter((s) => !a.slots.some((o) => iou(o.box, s.box) > 0.15) && ausenteEn(crudasDe.get(a.dia) || [], s.box, horas(s)) >= 3);
      // fotos de antes y de ahora a una hora parecida
      const par = (x, y) => ({ ...x, foto: fotoParecida(fotosDe.get(x.dia) || [], y.foto) || x.foto, cajas: [] });
      if (fueron.length) {
        const aa = { ...a, cajas: fueron.flatMap((s) => s.obs.filter((o) => o.p === a.foto).map((o) => o.d)) };
        avisos.push({ c, a: aa, b: par(b, aa), baja: true, texto: `${cap1(plural(fueron.length, m.uno, m.corto))} que había el ${fmtKey(a.dia)} ya no ${fueron.length === 1 ? 'está' : 'están'} el ${fmtKey(b.dia)}` });
      }
      if (llegaron.length) {
        const bb = { ...b, cajas: llegaron.flatMap((s) => s.obs.filter((o) => o.p === b.foto).map((o) => o.d)) };
        avisos.push({ c, a: par(a, bb), b: bb, baja: false, texto: `${m.fem ? 'Nuevas' : 'Nuevos'} en obra el ${fmtKey(b.dia)}: ${plural(llegaron.length, m.uno, m.corto)}` });
      }
    }
  }
  avisos.sort((x, y) => y.b.dia.localeCompare(x.b.dia) || (y.baja - x.baja));
  // Consumo: promedio de las bajas por día (solo días completos)
  const consumo = {};
  for (const c of presentes) {
    const xs = porClase[c].filter((x) => x.completo);
    if (xs.length < 2) continue;
    let baja = 0;
    for (let i = 1; i < xs.length; i++) baja += Math.max(0, MAT[c].pila ? xs[i - 1].area - xs[i].area : xs[i - 1].n - xs[i].n);
    consumo[c] = baja / (xs.length - 1);
  }
  // Día de referencia ("hoy"): el último con la jornada completa (si no hay ninguno, el último)
  let iHoy = dias.length - 1;
  for (let i = dias.length - 1; i >= 0; i--) if (dias[i][1].length >= DIA_COMPLETO) { iHoy = i; break; }
  return { dias: dias.map(([k]) => k), porClase, presentes, avisos, consumo, iHoy };
}
